import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { FiLoader, FiLock } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { useCourse } from "../context/CourseContext";
import { apiFetch } from "../utils/apiFetch";
import { communitySections } from "../utils/communitySections.mjs";
import { ownerPlan } from "../utils/communityEntitlements.mjs";
import PremiumSetupPopup from "./PremiumSetupPopup";

export default function CommunitySectionSettings() {
  const { course, setCourse } = useCourse();
  const { userId, userInfo } = useAuth();
  const client = useQueryClient();
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(null);
  const canLock = course.pricing?.access === "paid" && (course.store?.enabled ?? ownerPlan(userInfo?.subscriptionInfo) !== "default");
  const save = async (section, access, configured = false) => {
    if (saving) return;
    if (access === "premium" && !canLock && !configured) { setPending(section); return; }
    setSaving(section); setError("");
    try {
      const response = await apiFetch(`${process.env.API_URL}/api/groups/${course._id}/section-access`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ section, access }),
      });
      const data = await response.json();
      if (!response.ok) throw Error(data.message || "Could not update section access.");
      setCourse(previous => previous?._id === course._id ? { ...previous, sectionAccess: data.sectionAccess } : previous);
      await client.invalidateQueries({ queryKey: ["account", userId, "group", course.slug] });
    } catch (error) { setError(error.message); }
    finally { setSaving(null); }
  };
  return <div className="max-w-2xl">
    <h1 className="text-xl font-semibold">Channel access</h1>
    {!canLock && <p className="mt-3 text-sm text-[var(--app-secondary)]">A Plus or Pro plan and <Link className="underline" to="?setting=pricing">premium membership pricing</Link> are required for premium-only sections.</p>}
    {error && <p role="alert" className="mt-4 text-sm text-red-600">{error}</p>}
    <div className="mt-5 divide-y divide-[var(--app-border)]">
      {communitySections.filter(section => section.id !== "treasure").map(({ id, name }) => <div key={id} className="flex flex-wrap items-center justify-between gap-3 py-4">
        <label htmlFor={`section-${id}`} className="flex items-center gap-2 text-sm font-semibold">{name}{course.sectionAccess?.[id] === "premium" && <FiLock aria-label="Premium only" />}</label>
        <div className="flex items-center gap-2">
          {saving === id && <FiLoader role="status" aria-label={`Saving ${name} access`} className="animate-spin" />}
          <select id={`section-${id}`} value={course.sectionAccess?.[id] || "free"} disabled={Boolean(saving)} onChange={event => save(id, event.target.value)} className="min-h-10 rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] px-3 text-sm text-[var(--app-text)] disabled:opacity-60">
            <option value="free">All members</option>
            <option value="premium">Premium members</option>
          </select>
        </div>
      </div>)}
    </div>
    {pending && <PremiumSetupPopup onClose={() => setPending(null)} onComplete={() => save(pending, "premium", true)} />}
  </div>;
}
